import { useState, useMemo, useCallback, Fragment } from 'react';
import { T, useGT } from 'gt-react';
import {
  compareMoney,
  parseMoney,
  serializeMoney,
  subtractMoney,
  toMoneyNumber,
} from '@erp/shared-utils';
import { format } from 'date-fns';
import {
  flexRender,
  useTable,
  sortedAppTableFeatures,
  type ColumnDef,
  type SortingState,
} from '@/lib/tanstack-table';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { TooltipInfoIcon } from '@/components/common/TooltipInfoIcon';
import { CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import { useArInvoicesList } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { formatCurrency } from '@/components/common/Currency';
import { parseDateOnly } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';
import { ARInvoiceDetailSlidein } from '@/components/accounts-receivable/invoice-detail/ar-invoice-detail-slidein';
import { InvoiceSentCell } from '@/components/accounts-receivable/InvoiceSentCell';
import { CustomerLabel } from '@/components/customer/CustomerLabel';
import type {
  AgingBasis,
  AgingBucketDefinition,
  ArAgingCustomerRow,
  ArAgingTotals,
  ArInvoiceListItem,
  InvoiceStatus,
} from '@erp/rpc';

function fmtCurrency(value: string | null | undefined): string {
  return formatCurrency(value ?? '0', { currency: 'USD' });
}

function fmtDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = parseDateOnly(dateString);
  if (!date) return dateString;
  return format(date, 'MM/dd/yyyy');
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'draft':
      return (
        <Badge variant="secondary">
          <T context="ar aging invoice status draft">Draft</T>
        </Badge>
      );
    case 'open':
      return (
        <Badge variant="secondary">
          <T context="ar aging invoice status open">Open</T>
        </Badge>
      );
    case 'closed':
      return (
        <Badge variant="secondary">
          <T context="ar aging invoice status closed">Closed</T>
        </Badge>
      );
    case 'voided':
      return (
        <Badge variant="secondary">
          <T context="ar aging invoice status voided">Voided</T>
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function ColumnHeaderWithTooltip({
  label,
  description,
}: {
  label: string;
  description: string;
}) {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {label}
      <TooltipInfoIcon
        description={description}
        ariaLabel={`More information about ${label}`}
      />
    </span>
  );
}

// ── Bucket-based invoice filtering (derived from bucket definitions) ───

/**
 * Compute date range filters for the invoice list query based on
 * a bucket definition and aging basis.
 * bucketIndex=-1 means "all" (no filter).
 */
function bucketToDateFilters(
  bucketIndex: number,
  bucketDefinitions: AgingBucketDefinition[],
  basis: AgingBasis,
): {
  agingBasis?: AgingBasis;
  agingDateFrom?: string;
  agingDateTo?: string;
} {
  if (bucketIndex < 0) return {};

  const bucket = bucketDefinitions[bucketIndex];
  if (!bucket) return {};

  const today = new Date();
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  const daysAgo = (n: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };

  let from: string | undefined;
  let to: string | undefined;

  if (bucket.minDays === 0) {
    // "Current" — not yet due
    from = toISO(today);
  } else if (bucket.maxDays != null) {
    // Bounded range
    from = toISO(daysAgo(bucket.maxDays));
    to = toISO(daysAgo(bucket.minDays));
  } else {
    // Open-ended (e.g. 91+)
    to = toISO(daysAgo(bucket.minDays));
  }

  return { agingBasis: basis, agingDateFrom: from, agingDateTo: to };
}

function daysRangeToDateFilters(
  minDays: number | undefined,
  maxDays: number | undefined,
  basis: AgingBasis,
): {
  agingBasis?: AgingBasis;
  agingDateFrom?: string;
  agingDateTo?: string;
} {
  if (minDays === undefined && maxDays === undefined) return {};

  const today = new Date();
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  const daysAgo = (n: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };

  const min = minDays ?? 0;
  let from: string | undefined;
  let to: string | undefined;

  if (min === 0 && maxDays === 0) {
    from = toISO(today);
  } else if (min === 0 && maxDays !== undefined) {
    from = toISO(daysAgo(maxDays));
  } else if (maxDays !== undefined) {
    from = toISO(daysAgo(maxDays));
    to = toISO(daysAgo(min));
  } else if (min > 0) {
    to = toISO(daysAgo(min));
  }

  return { agingBasis: basis, agingDateFrom: from, agingDateTo: to };
}

// ── Invoice detail sort ──────────────────────────────────────────────────

type InvoiceDetailSortBy =
  | 'invoiceNumber'
  | 'dueDate'
  | 'shipDate'
  | 'netAmount'
  | 'appliedAmount'
  | 'balanceDue'
  | 'status'
  | 'firstSentAt';

function SortableDetailHeader({
  column,
  label,
  sortBy,
  sortDir,
  onSort,
  className,
  children,
}: {
  column: InvoiceDetailSortBy;
  label: string;
  sortBy: InvoiceDetailSortBy;
  sortDir: 'asc' | 'desc';
  onSort: (column: InvoiceDetailSortBy) => void;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <td
      className={cn(
        'py-1.5 text-xs font-medium text-muted-foreground',
        className,
      )}
    >
      <button
        type="button"
        className="inline-flex items-center gap-0.5 cursor-pointer select-none hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onSort(column);
        }}
      >
        {children ?? label}
        {sortBy === column && (
          <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </button>
    </td>
  );
}

function sortInvoices(
  invoices: ArInvoiceListItem[],
  sortBy: InvoiceDetailSortBy,
  sortDir: 'asc' | 'desc',
): ArInvoiceListItem[] {
  return [...invoices].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'invoiceNumber':
        return (
          (a.invoiceNumber ?? '').localeCompare(b.invoiceNumber ?? '') * dir
        );
      case 'dueDate':
        return (a.dueDate ?? '').localeCompare(b.dueDate ?? '') * dir;
      case 'shipDate':
        return (a.shipDate ?? '').localeCompare(b.shipDate ?? '') * dir;
      case 'netAmount':
        return compareMoney(a.netAmount ?? '0', b.netAmount ?? '0') * dir;
      case 'appliedAmount': {
        const aApplied = subtractMoney(a.netAmount ?? '0', a.balanceDue ?? '0');
        const bApplied = subtractMoney(b.netAmount ?? '0', b.balanceDue ?? '0');
        return compareMoney(aApplied, bApplied) * dir;
      }
      case 'balanceDue':
        return compareMoney(a.balanceDue ?? '0', b.balanceDue ?? '0') * dir;
      case 'status':
        return (a.status ?? '').localeCompare(b.status ?? '') * dir;
      case 'firstSentAt': {
        // Treat unsent (null) as infinitely old — sorts first ascending,
        // last descending, mirroring the SQL ORDER BY in InvoiceService.
        if (!a.firstSentAt && !b.firstSentAt) return 0;
        if (!a.firstSentAt) return -1 * dir;
        if (!b.firstSentAt) return 1 * dir;
        return a.firstSentAt.localeCompare(b.firstSentAt) * dir;
      }
      default:
        return 0;
    }
  });
}

// ── Lazy-loaded invoice sub-table for an expanded customer ──────────────

function CustomerInvoiceDetail({
  customerId,
  agingBasis,
  columnCount,
  bucketDefinitions,
  onInvoiceSelect,
  statusFilter,
  pageLevelMinDays,
  pageLevelMaxDays,
}: {
  customerId: string;
  agingBasis: AgingBasis;
  columnCount: number;
  bucketDefinitions: AgingBucketDefinition[];
  onInvoiceSelect: (invoiceId: string) => void;
  statusFilter?: InvoiceStatus[];
  pageLevelMinDays?: number;
  pageLevelMaxDays?: number;
}) {
  const gt = useGT();
  const [detailSortBy, setDetailSortBy] =
    useState<InvoiceDetailSortBy>('dueDate');
  const [detailSortDir, setDetailSortDir] = useState<'asc' | 'desc'>('asc');
  const handleDetailSort = useCallback(
    (column: InvoiceDetailSortBy) => {
      if (detailSortBy === column) {
        setDetailSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setDetailSortBy(column);
        setDetailSortDir(
          column === 'invoiceNumber' || column === 'status' ? 'asc' : 'desc',
        );
      }
    },
    [detailSortBy],
  );

  // -1 = "all" (no bucket filter)
  const [activeBucketIdx, setActiveBucketIdx] = useState(-1);
  const bucketDateFilters = bucketToDateFilters(
    activeBucketIdx,
    bucketDefinitions,
    agingBasis,
  );
  const pageDateFilters = daysRangeToDateFilters(
    pageLevelMinDays,
    pageLevelMaxDays,
    agingBasis,
  );
  const dateFilters =
    activeBucketIdx >= 0 ? bucketDateFilters : pageDateFilters;

  const { invoices, isLoading } = useArInvoicesList({
    customerId: [customerId],
    status: statusFilter ?? ['draft', 'open'],
    sortBy: 'dueDate',
    sortDirection: 'asc',
    limit: 100,
    ...dateFilters,
  });

  const sortedInvoices = useMemo(
    () => sortInvoices(invoices, detailSortBy, detailSortDir),
    [invoices, detailSortBy, detailSortDir],
  );

  return (
    <>
      {/* Bucket filter bar */}
      <tr className="bg-muted/30">
        <td colSpan={columnCount} className="px-10 py-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground mr-1">
              <T context="ar aging detail show label">Show:</T>
            </span>
            <ToggleGroup
              type="single"
              value={String(activeBucketIdx)}
              onValueChange={(v) => {
                if (v) setActiveBucketIdx(Number(v));
              }}
              size="sm"
            >
              <ToggleGroupItem value="-1" className="px-2 h-6 text-xs">
                <T context="ar aging detail show all option">All</T>
              </ToggleGroupItem>
              {bucketDefinitions.map((def, i) => (
                <ToggleGroupItem
                  key={i}
                  value={String(i)}
                  className={cn(
                    'h-6 text-xs',
                    def.label === 'Current' ? 'px-2' : 'px-3',
                  )}
                >
                  {def.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </td>
      </tr>

      {isLoading ? (
        <tr>
          <td colSpan={columnCount} className="p-0">
            <div className="bg-muted/30 px-10 py-3 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full max-w-2xl" />
              ))}
            </div>
          </td>
        </tr>
      ) : invoices.length === 0 ? (
        <tr>
          <td colSpan={columnCount} className="p-0">
            <div className="bg-muted/30 px-10 py-4 text-sm text-muted-foreground">
              {activeBucketIdx < 0 ? (
                <T context="ar aging no open invoices">
                  No open invoices found
                </T>
              ) : (
                <T context="ar aging no invoices in bucket">
                  No invoices in this aging bucket
                </T>
              )}
            </div>
          </td>
        </tr>
      ) : (
        <>
          {/* Sub-header */}
          <tr className="bg-muted/30">
            <SortableDetailHeader
              column="invoiceNumber"
              label={gt('Invoice #', {
                context: 'ar aging detail invoice number column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="pl-10"
            />
            <SortableDetailHeader
              column="firstSentAt"
              label={gt('Sent Date', {
                context: 'ar aging detail sent date column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            />
            <SortableDetailHeader
              column="dueDate"
              label={gt('Due Date', {
                context: 'ar aging detail due date column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            />
            <SortableDetailHeader
              column="shipDate"
              label={gt('Ship Date', {
                context: 'ar aging detail ship date column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            />
            <SortableDetailHeader
              column="netAmount"
              label={gt('Amount', {
                context: 'ar aging detail amount column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            >
              <ColumnHeaderWithTooltip
                label={gt('Amount', {
                  context: 'ar aging detail amount tooltip label',
                })}
                description="Net invoice amount after line-level adjustments. Payments and credit/debit memos are reflected in the Applied column instead."
              />
            </SortableDetailHeader>
            <SortableDetailHeader
              column="appliedAmount"
              label={gt('Applied', {
                context: 'ar aging detail applied column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            >
              <ColumnHeaderWithTooltip
                label={gt('Applied', {
                  context: 'ar aging detail applied tooltip label',
                })}
                description="How much the invoice has been reduced by, including both payments received AND any credit/debit adjustments. Computed as Amount − Balance."
              />
            </SortableDetailHeader>
            <SortableDetailHeader
              column="balanceDue"
              label={gt('Balance', {
                context: 'ar aging detail balance column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            >
              <ColumnHeaderWithTooltip
                label={gt('Balance', {
                  context: 'ar aging detail balance tooltip label',
                })}
                description="Remaining amount owed. This is the value that feeds the aging buckets above."
              />
            </SortableDetailHeader>
            <SortableDetailHeader
              column="status"
              label={gt('Status', {
                context: 'ar aging detail status column label',
              })}
              sortBy={detailSortBy}
              sortDir={detailSortDir}
              onSort={handleDetailSort}
              className="text-right"
            />
            {columnCount > 8 ? <td colSpan={columnCount - 8} /> : null}
          </tr>
          {sortedInvoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              invoice={inv}
              onSelect={onInvoiceSelect}
              columnCount={columnCount}
            />
          ))}
        </>
      )}
    </>
  );
}

function InvoiceRow({
  invoice,
  onSelect,
  columnCount,
}: {
  invoice: ArInvoiceListItem;
  onSelect: (invoiceId: string) => void;
  columnCount: number;
}) {
  return (
    <tr
      className="bg-muted/30 border-t border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => onSelect(invoice.id)}
    >
      <td className="pl-10 py-1.5 text-xs">
        <span className="text-primary hover:underline font-medium">
          {invoice.invoiceNumber ?? '—'}
        </span>
      </td>
      <td
        className="py-1.5 text-xs text-muted-foreground text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <InvoiceSentCell
          firstSentAt={invoice.firstSentAt}
          sendEvents={invoice.sendEvents}
        />
      </td>
      <td className="py-1.5 text-xs text-muted-foreground text-right">
        {fmtDate(invoice.dueDate)}
      </td>
      <td className="py-1.5 text-xs text-muted-foreground text-right">
        {fmtDate(invoice.shipDate)}
      </td>
      <td className="py-1.5 text-xs text-right font-medium">
        {invoice.netAmount != null ? fmtCurrency(invoice.netAmount) : '—'}
      </td>
      <td className="py-1.5 text-xs text-right font-medium">
        {invoice.netAmount != null && invoice.balanceDue != null
          ? fmtCurrency(
              serializeMoney(
                subtractMoney(invoice.netAmount, invoice.balanceDue),
              ),
            )
          : '—'}
      </td>
      <td className="py-1.5 text-xs text-right font-medium">
        {invoice.balanceDue != null ? fmtCurrency(invoice.balanceDue) : '—'}
      </td>
      <td className="py-1.5 text-xs text-right">
        {getStatusBadge(invoice.status)}
      </td>
      {columnCount > 8 ? <td colSpan={columnCount - 8} /> : null}
    </tr>
  );
}

// ── Main table component ────────────────────────────────────────────────

interface ARAgingDetailTableProps {
  customers: ArAgingCustomerRow[];
  totals: ArAgingTotals | null;
  isLoading: boolean;
  agingBasis: AgingBasis;
  bucketDefinitions: AgingBucketDefinition[];
  statusFilter?: InvoiceStatus[];
  minDays?: number;
  maxDays?: number;
}

export function ARAgingDetailTable({
  customers,
  totals,
  isLoading,
  agingBasis,
  bucketDefinitions,
  statusFilter,
  minDays,
  maxDays,
}: ARAgingDetailTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'totalOutstanding', desc: true },
  ]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );

  const toggleExpanded = (customerId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) {
        next.delete(customerId);
      } else {
        next.add(customerId);
      }
      return next;
    });
  };

  // Generate bucket columns dynamically from definitions
  const bucketColumns = useMemo(
    (): ColumnDef<ArAgingCustomerRow>[] =>
      bucketDefinitions.map((def, i) => {
        // Mark the last (open-ended) bucket with red highlight
        const isOpenEnded = def.maxDays == null && def.minDays > 0;
        return {
          id: `bucket_${i}`,
          accessorFn: (row) =>
            toMoneyNumber(parseMoney(row.bucketAmounts[i] ?? '0')),
          header: def.label,
          cell: ({ row }) => {
            const value = row.original.bucketAmounts[i] ?? '0';
            return isOpenEnded ? (
              <span
                className={cn(compareMoney(value, 0) > 0 && 'text-red-600')}
              >
                {fmtCurrency(value)}
              </span>
            ) : (
              fmtCurrency(value)
            );
          },
          meta: { align: 'right' },
        };
      }),
    [bucketDefinitions],
  );

  const columns = useMemo(
    (): ColumnDef<ArAgingCustomerRow>[] => [
      {
        id: 'customerName',
        accessorFn: (row) => row.customerName ?? '',
        header: 'Customer',
        size: 240,
        cell: ({ row }) => {
          const isExpanded = expandedIds.has(row.original.customerId);
          return (
            <button
              type="button"
              className="flex items-center gap-1.5 w-full text-left font-medium"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(row.original.customerId);
              }}
            >
              {isExpanded ? (
                <CaretDownIcon className="size-4 shrink-0" />
              ) : (
                <CaretRightIcon className="size-4 shrink-0" />
              )}
              <span className="truncate">
                {row.original.customerName || row.original.customerId ? (
                  <CustomerLabel
                    erpDisplayName={row.original.erpDisplayName}
                    fallback={row.original.customerName}
                    className="text-inherit"
                  />
                ) : (
                  <T context="ar aging unknown customer name">(Unknown)</T>
                )}
              </span>
            </button>
          );
        },
      },
      ...bucketColumns,
      {
        id: 'totalOutstanding',
        accessorFn: (row) => toMoneyNumber(parseMoney(row.totalOutstanding)),
        header: 'Total',
        cell: ({ row }) => (
          <span className="font-semibold">
            {fmtCurrency(row.original.totalOutstanding)}
          </span>
        ),
        meta: { align: 'right' },
      },
      {
        id: 'invoiceCount',
        accessorFn: (row) => row.invoiceCount,
        header: 'Invoices',
        cell: ({ row }) => row.original.invoiceCount,
        meta: { align: 'right' },
      },
    ],
    [expandedIds, bucketColumns],
  );

  const table = useTable({
    features: sortedAppTableFeatures,
    data: customers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (row) => row.customerId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <T context="ar aging detail table title">Aging Detail</T>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align =
                    (header.column.columnDef.meta as { align?: string })
                      ?.align ?? 'left'; // eslint-disable-line i18next/no-literal-string -- CSS alignment value
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        header.id === 'customerName' && 'min-w-[200px]',
                        header.id === 'totalOutstanding' && 'font-semibold',
                        align === 'right' && 'text-right',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none hover:bg-muted/50',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() === 'asc' && ' ↑'}
                        {header.column.getIsSorted() === 'desc' && ' ↓'}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {Array.from({ length: columns.length }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton
                        className={cn('h-4', j === 0 ? 'w-40' : 'w-20 ml-auto')}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  <T context="AR aging detail table empty state">
                    No aging data available
                  </T>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isExpanded = expandedIds.has(row.original.customerId);
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        isExpanded && 'bg-muted/30',
                      )}
                      onClick={() => toggleExpanded(row.original.customerId)}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const align =
                          (cell.column.columnDef.meta as { align?: string })
                            ?.align ?? 'left'; // eslint-disable-line i18next/no-literal-string -- CSS alignment value
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(align === 'right' && 'text-right')}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <CustomerInvoiceDetail
                        customerId={row.original.customerId}
                        agingBasis={agingBasis}
                        columnCount={columns.length}
                        bucketDefinitions={bucketDefinitions}
                        onInvoiceSelect={setSelectedInvoiceId}
                        statusFilter={statusFilter}
                        pageLevelMinDays={minDays}
                        pageLevelMaxDays={maxDays}
                      />
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
          {!isLoading && totals && (
            <TableFooter>
              <TableRow className="font-semibold bg-muted/50">
                <TableCell>
                  <T context="AR aging detail table total row label">Total</T>
                </TableCell>
                {bucketDefinitions.map((_, i) => (
                  <TableCell key={i} className="text-right">
                    {fmtCurrency(totals.bucketAmounts[i] ?? '0')}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  {fmtCurrency(totals.totalOutstanding)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
      {selectedInvoiceId && (
        <ARInvoiceDetailSlidein
          invoiceId={selectedInvoiceId}
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) setSelectedInvoiceId(null);
          }}
        />
      )}
    </Card>
  );
}
